import React, { useEffect } from "react";
import Dropdown from "./Dropdown";
import InstructionsPanel from "./InstructionsPanel";
import { useCameraCheck } from "../hooks/useCameraCheck";
import Webcam from "react-webcam";

import default_camera_bg from "../assets/default_camera_bg.png";
import check_circle from "../assets/check_circle.svg";
import person from "../assets/person-skeleton.svg";

interface CameraCheckProps {
  onCameraChange: (isCameraSelected: boolean) => void;
  onScaleChange: (scale: number) => void;
  instructions: string[];
  onCameraStreamChange?: (stream: MediaStream | null) => void;
  footer?: React.ReactNode;
}

const CameraCheck: React.FC<CameraCheckProps> = ({
  onCameraChange,
  onScaleChange,
  instructions,
  onCameraStreamChange,
  footer,
}) => {
  const {
    cameras,
    selectedCamera,
    isCameraReady,
    zoom,
    videoRef,
    handleCameraChange,
    handleZoomChange,
  } = useCameraCheck();

  const webcamRef = React.useRef<Webcam>(null);

  // Notify parent component of camera readiness status (not just selection)
  React.useEffect(() => {
    onCameraChange(isCameraReady);
  }, [isCameraReady, onCameraChange]);

  // Notify parent component of zoom scale
  React.useEffect(() => {
    onScaleChange(zoom / 100);
  }, [zoom, onScaleChange]);

  React.useEffect(() => {
    if (!selectedCamera) {
      onCameraStreamChange?.(null);
    }
  }, [selectedCamera, onCameraStreamChange]);

  const handleUserMedia = React.useCallback(
    (stream: MediaStream) => {
      onCameraStreamChange?.(stream);
    },
    [onCameraStreamChange]
  );

  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current = null;
      }
    };
  }, [videoRef]);
  return (
    <div className="bg-[#FAFAFA] p-8 flex flex-col gap-6 h-fit rounded-xl border-2 border-[#DBDBDB] w-full">
      <div className="flex justify-between items-start gap-[50px] flex-row flex-wrap">
        <div className="flex flex-col gap-4">
          <div className="flex flex-row gap-2 items-center justify-between">
            <Dropdown
              options={cameras.map((camera) => ({
                label: camera.label || `Camera ${camera.deviceId}`,
                value: camera.deviceId,
              }))}
              value={selectedCamera || ""}
              placeholder="Select Video Source"
              onChange={handleCameraChange}
              width={40}
              dropdownWidth={40}
              className=" max-w-[240px]"
            />
            {isCameraReady && (
              <div className=" flex flex-row gap-2 bg-[#DBFFEA] items-center justify-center border border-[#1FD167] rounded-full pl-[10px] pr-[14px] py-2">
                <div>
                  <img
                    src={check_circle}
                    alt="checked"
                    className="w-[20px] h-[20px]"
                  />
                </div>
                <div className="text-[#03963F] font-dm-sans text-base font-medium leading-normal">
                  Checked
                </div>
              </div>
            )}
          </div>

          <div className=" flex  min-w-[360px] max-h-[228px] items-center justify-center  border-2 border-[#DBDBDB] rounded-xl relative">
            {selectedCamera ? (
              <>
                <img
                  className=" w-[40vh]  h-full absolute z-10 left-1/2 transform -translate-x-1/2 bottom-0"
                  src={person}
                  alt="Head Skeleton"
                />
                <div
                  className="relative w-full h-full max-h-[228px] flex items-center justify-center rounded-[10px] xl:max-h-[23vh]"
                  style={{ overflow: "hidden" }}
                >
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    videoConstraints={{
                      deviceId: selectedCamera,
                      width: 1280,
                      facingMode: "user",
                    }}
                    onUserMedia={handleUserMedia}
                    onUserMediaError={() => onCameraStreamChange?.(null)}
                    className="border-0 transform -scale-x-100"
                    style={{
                      transform: `scale(${zoom / 100}) scaleX(-1)`,
                      transition: "transform 0.3s ease",
                      objectFit: "cover",
                      width: "100%",
                      height: "100%",
                      maxHeight: "228px",
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center overflow-hidden rounded-xl">
                <img
                  src={default_camera_bg}
                  alt="default_camera_bg"
                  className="w-[360px] h-[226px] object-cover xl:max-h-[23vh]"
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <div className="flex justify-between">
              <span className="text-[#4C4C4C] text-sm font-medium">
                Camera Zoom
              </span>
              <span className="text-[#4C4C4C] text-sm font-medium">
                {zoom - 100}%
              </span>
            </div>
            <input
              type="range"
              min="100"
              max="300"
              step="2"
              value={zoom}
              onChange={(e) => handleZoomChange(Number(e.target.value))}
              className="w-full h-1.5 bg-gray-200 rounded appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #001630 ${
                  (zoom - 100) / 2
                }%, #ddd ${(zoom - 100) / 2}%)`,
              }}
            />
            {/* Custom Thumb Style */}
            <style>{`
            input[type="range"]::-webkit-slider-thumb {
              background-color: white;
              border: 1px solid #0000001A;
              width: 11px; /* Adjust thumb size */
              height: 11px; /* Adjust thumb size */
              border-radius: 50%; /* Make thumb circular */
              cursor: pointer; /* Keep pointer cursor */
              appearance: none; /* Remove default appearance */
            }

            input[type="range"]::-moz-range-thumb {
              background-color: white; 
              border: 1px solid #0000001A; /* Optional: Add green border around the thumb */
              width: 11px;
              height: 11px;
              border-radius: 50%;
              cursor: pointer;
            }
          `}</style>
          </div>
        </div>
        <div className="flex flex-col flex-1">
          <InstructionsPanel instructions={instructions} />
        </div>
      </div>

      {footer && <div className="mt-4 w-full">{footer}</div>}
    </div>
  );
};

export default CameraCheck;
